import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontSize } from "style";
import { Row, Col } from "components/Containers";
import TextInput from "components/Form/TextInput";
import Select from "components/Form/Select";
import MinMaxInput from "components/Form/MinMaxInput";
import { CLIENT_BUDGET_RATE_FREQUENCIES, CURRENCIES, CURRENCY_NAMES, RATE_FREQUENCIES } from "constants/index";

/**
 * RateInput
 *
 * @param {String}   name
 * @param {Object}   value
 * @param {Function} onChange
 * @param {Boolean}  isClearable
 * @param {Array}    currencyOptions
 * @param {Array}    frequencyOptions
 * @param {String}   defaultCurrency
 * @param {String}   defaultFrequency
 * @param {Boolean}  isRateRange
 * @param {Object}   disableFrequency
 * @param {String}   rateKeyName
 */
const RateInput = ({
  name,
  value,
  onChange,
  isClearable = false,
  currencyOptions = CURRENCIES,
  frequencyOptions = CLIENT_BUDGET_RATE_FREQUENCIES,
  defaultCurrency = CURRENCY_NAMES.cad,
  defaultFrequency = RATE_FREQUENCIES.hourly,
  isRateRange = false,
  disableFrequency = false,
  rateKeyName = "rate",
  ...props
}) => {
  const [rate, setRate] = useState(value?.[rateKeyName] || "");
  const [currency, setCurrency] = useState(value?.currency || defaultCurrency);
  const [frequency, setFrequency] = useState(value?.frequency || defaultFrequency);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (value) {
      setRate(value[rateKeyName] || "");
      setCurrency(value.currency || defaultCurrency);
      setFrequency(value.frequency || defaultFrequency);
    }
  }, [value, defaultCurrency, defaultFrequency]);

  useEffect(() => {
    if (focus) return;

    onChange(name, {
      [rateKeyName]: rate,
      currency,
      frequency,
    });
  }, [rate, currency, frequency, name, focus]);

  // Handle value reset when isRateRange prop changes
  useEffect(() => {
    if (isRateRange && typeof rate !== "object") {
      setRate({ min: "", max: "" });
    } else if (!isRateRange && typeof rate === "object") {
      setRate("");
    }
  }, [isRateRange]);

  const handleValueChange = (fieldName, val) => setRate(val);
  const handleCurrencyChange = (fieldName, val) => setCurrency(val);
  const handleFrequencyChange = (fieldName, val) => setFrequency(val);
  const handleFocus = () => setFocus(true);
  const handleBlur = () => setFocus(false);

  return (
    <div css={styles.container}>
      <Col gap="1rem">
        {/* Main Input Row */}
        <Row gap="0.5rem" css={styles.inputRow}>
          {/* Rate Value Input */}
          <div css={styles.valueContainer}>
            {isRateRange ? (
              <MinMaxInput
                name={rateKeyName}
                value={rate}
                onChange={handleValueChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                isClearable={isClearable}
                component={TextInput}
                css={styles.rangeInput}
                maxInputCss={styles.maxInput}
                {...props}
              />
            ) : (
              <TextInput
                name={rateKeyName}
                value={rate}
                onChange={handleValueChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={`Enter ${rateKeyName}`}
                css={styles.valueInput}
                {...props}
              />
            )}
          </div>

          {/* Currency Select */}
          <div css={styles.selectContainer}>
            <CurrencySelect
              name="currency"
              value={currency}
              onChange={handleCurrencyChange}
              options={currencyOptions}
              placeholder="Currency"
              css={styles.currencySelect}
            />
          </div>

          {/* Frequency Select */}
          <div css={styles.selectContainer}>
            <FrequencySelect
              name="frequency"
              value={frequency}
              onChange={handleFrequencyChange}
              options={frequencyOptions}
              placeholder="Per"
              css={styles.frequencySelect}
              disabled={disableFrequency}
            />
          </div>
        </Row>
      </Col>
    </div>
  );
};

const CurrencySelect = styled(Select)`
  div[class*="-control"] {
    border-right: none;
    border-left: none;
    border-radius: 0;
  }
`;

const FrequencySelect = styled(Select)`
  div[class*="-control"] {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`;

const styles = {
  container: css`
    position: relative;
    order: 1;
    width: 100%;
  `,
  inputRow: css`
    align-items: center;
    width: 100%;
    gap: 0;
  `,
  valueContainer: css`
    flex: 2;
    min-width: 0;
  `,
  valueInput: css`
    width: 100%;
    font-size: ${fontSize.xsmall};
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `,
  rangeInput: css`
    width: 100%;
  `,
  maxInput: css`
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `,
  selectContainer: css`
    flex: 1;
  `,
  currencySelect: css`
    width: 80px;
  `,
  frequencySelect: css`
    width: 95px;
  `,
};

RateInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  isClearable: PropTypes.bool,
  currencyOptions: PropTypes.array,
  frequencyOptions: PropTypes.array,
  defaultCurrency: PropTypes.string,
  defaultFrequency: PropTypes.string,
  isRateRange: PropTypes.bool,
  disableFrequency: PropTypes.bool,
  rateKeyName: PropTypes.string,
};

export default RateInput;
